import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FinalCTA from '@/components/FinalCTA';
import ScrollToTop from '@/components/ScrollToTop';
import { ArrowRight, Zap, ShieldCheck, FileText, Sparkles, MessageSquare, Users, Eye, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import BlogSection from '@/components/BlogSection';
import { blogPosts } from '@/data/blog-posts';
import SEO from '@/components/SEO';

// Helper to detect server environment
const isServer = typeof window === 'undefined';

const Index = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = React.useState(false);
  const [mousePosition, setMousePosition] = React.useState({ x: 0.5, y: 0.5 });
  const [isMouseInSection, setIsMouseInSection] = React.useState(false);

  useEffect(() => {
    // Smooth scroll to sections when clicking on hash links
    const handleHashLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        const targetId = target.getAttribute('href')?.substring(1);
        const targetElement = document.getElementById(targetId || '');

        if (targetElement) {
          e.preventDefault();
          window.scrollTo({
            top: targetElement.offsetTop - 80, // Adjust for navbar height
            behavior: 'smooth'
          });
        }
      }
    };

    document.addEventListener('click', handleHashLinkClick);

    // Add mouse tracking effect like homepage
    if (!heroRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;

      const { clientX, clientY } = e;
      const { left, top, width, height, bottom } = heroRef.current.getBoundingClientRect();

      const isInSection = clientY >= top && clientY <= bottom;
      setIsMouseInSection(isInSection);

      if (!isInSection) return;

      const x = (clientX - left) / width;
      const y = (clientY - top) / height;

      const containerCenterX = 0.5;
      const distanceFromCenter = Math.abs(x - containerCenterX);

      if (distanceFromCenter < 0.6) {
        const moveX = (x - 0.5) * 15; // Reduced sensitivity
        const moveY = (y - 0.5) * 15; // Reduced sensitivity

        setMousePosition({ x, y });

        heroRef.current.style.setProperty('--x-offset', `${moveX}px`);
        heroRef.current.style.setProperty('--y-offset', `${moveY}px`);
      }
    };

    const handleMouseLeave = () => {
      setIsMouseInSection(false);

      // Reset the grid position smoothly when mouse leaves
      if (heroRef.current) {
        heroRef.current.style.setProperty('--x-offset', '0px');
        heroRef.current.style.setProperty('--y-offset', '0px');
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    heroRef.current?.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('click', handleHashLinkClick);
      document.removeEventListener('mousemove', handleMouseMove);
      heroRef.current?.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Defer video loading until after page is fully loaded
  useEffect(() => {
    const loadVideo = () => {
      setVideoReady(true);
    };

    if (document.readyState === 'complete') {
      loadVideo();
    } else {
      window.addEventListener('load', loadVideo);
      return () => window.removeEventListener('load', loadVideo);
    }
  }, []);

  const getBgColor = () => {
    if (!isMouseInSection) {
      return 'hsl(240, 80%, 7%)';
    }

    const baseHue = 240;
    const xHueShift = mousePosition.x * 20;
    const yHueShift = mousePosition.y * 10;

    const hue = baseHue + xHueShift - yHueShift;
    const saturation = 80 + mousePosition.y * 20;
    const lightness = 5 + mousePosition.x * 5;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const getGlowColor = () => {
    const baseHue = 210;
    const hueShift = (mousePosition.x - mousePosition.y) * 30;
    return `hsl(${baseHue + hueShift}, 80%, 50%)`;
  };

  const benefits = [
    {
      title: "No Selectors to Maintain",
      description: "AI adapts to UI changes automatically. No more broken tests from minor frontend updates.",
      icon: Zap
    },
    {
      title: "Multi-User Workflow Testing",
      description: "Test buyer + seller, patient + doctor, student + teacher flows in a single coordinated test.",
      icon: Users
    },
    {
      title: "Cloud or Self-Hosted",
      description: "Use our managed cloud or deploy on your own infrastructure. Full flexibility with complete data control either way.",
      icon: ShieldCheck
    },
    {
      title: "Open Source",
      description: "MIT licensed, no vendor lock-in. Inspect the code, contribute, and customize to your needs.",
      icon: FileText
    },
    {
      title: "Hybrid Steps",
      description: "Mix plain English with Playwright code when you need precision. Use whichever fits the job.",
      icon: Sparkles
    },
    {
      title: "Real-Time Results",
      description: "Live streaming of test execution with screenshots. Watch your tests run in real-time.",
      icon: MessageSquare
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Describe Your Test",
      description: "Write what you want to test in plain English. 'Sign in as admin, navigate to orders, verify the latest order exists.'"
    },
    {
      number: "02",
      title: "Configure Browsers & Roles",
      description: "Set up multiple browsers with different URLs and credentials. One browser for the customer, another for the admin."
    },
    {
      number: "03",
      title: "Run & Get Results",
      description: "Execute tests and get screenshot evidence for every step, detailed pass/fail reports, and real-time streaming of results."
    }
  ];

  // Calculate grid size for background effect
  const gridSize = 60;
  // Server-safe grid dots implementation
  const gridDots = React.useMemo(() => {
    if (isServer) {
      // Return empty array when rendering on server
      return [];
    }

    return Array.from({ length: 20 }).map(() => {
      const gridCellsX = Math.floor(window.innerWidth / gridSize);
      const gridCellsY = Math.floor(window.innerHeight / gridSize);

      const cellX = Math.floor(Math.random() * gridCellsX);
      const cellY = Math.floor(Math.random() * gridCellsY);

      return {
        x: cellX * gridSize,
        y: cellY * gridSize,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.5 + 0.2,
        duration: Math.random() * 10 + 10,
        delay: Math.random() * 5
      };
    });
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-dark-bg relative">
      <SEO
        title="SkyTest.ai - Write Tests in Plain English. Test Real Multi-User Workflows."
        description="The only AI test platform that coordinates multiple browsers and roles in a single test case. Write tests in plain English, SkyTest does the rest."
      />
      {/* Global background effect */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,34,255,0.05)_0%,rgba(10,10,20,0)_70%)] pointer-events-none"></div>

      <Navbar />

      <main className="flex-grow relative">
        {/* Hero Section with dynamic background */}
        <section
          ref={heroRef}
          id="hero"
          className="min-h-[65vh] relative flex items-center justify-center section-padding overflow-hidden pb-6"
          style={{
            '--x-offset': '0px',
            '--y-offset': '0px',
          } as React.CSSProperties}
        >
          <div
            className="absolute inset-0 overflow-hidden transition-colors duration-500 ease-out"
            style={{
              background: `linear-gradient(to bottom, ${getBgColor()}, #0a0a14)`,
            }}
          >
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="circuit-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                    <path d="M0,50 H100 M50,0 V100 M25,25 L75,75 M75,25 L25,75" stroke="white" strokeWidth="0.5" fill="none" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#circuit-pattern)" />
              </svg>
            </div>

            <div
              className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[65%] h-[45%] rounded-full blur-[100px]"
              style={{
                background: `radial-gradient(circle at center, ${getGlowColor()} 0%, rgba(0,34,255,0.05) 70%)`,
                opacity: 0.4,
                animation: 'pulse-subtle 8s ease-in-out infinite'
              }}
            />

            <div
              className="absolute bottom-[15%] left-1/2 -translate-x-1/2 w-[50%] h-[30%] rounded-full blur-[80px]"
              style={{
                background: 'radial-gradient(circle at center, rgba(0,100,255,0.4) 0%, rgba(0,34,255,0.05) 70%)',
                opacity: 0.2,
                animation: 'pulse-subtle 10s ease-in-out infinite',
                animationDelay: '2s'
              }}
            />

            <div
              className="absolute inset-0 opacity-30"
              style={{
                background: 'radial-gradient(circle at 50% 30%, rgba(0,34,255,0.15) 0%, rgba(10,10,20,0) 60%)',
              }}
            />
          </div>

          <div
            className="absolute inset-0 opacity-[0.03] will-change-transform"
            style={{
              backgroundImage: 'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
              backgroundSize: `${gridSize}px ${gridSize}px`,
              backgroundPosition: `calc(50% + var(--x-offset, 0px)) calc(50% + var(--y-offset, 0px))`,
              transition: 'background-position 0.1s ease-out',
            }}
          />

          <div className="absolute inset-0 overflow-hidden">
            {gridDots.map((dot, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-white/30"
                style={{
                  width: `${dot.size}px`,
                  height: `${dot.size}px`,
                  left: dot.x,
                  top: dot.y,
                  opacity: dot.opacity,
                  animation: `float ${dot.duration}s linear infinite`,
                  animationDelay: `${dot.delay}s`,
                  transform: 'translate(var(--x-offset), var(--y-offset))',
                  transition: 'transform 0.1s ease-out',
                }}
              />
            ))}
          </div>

          <div className="container max-w-6xl mx-auto flex flex-col items-center text-center z-10">
            <div className="inline-block animate-fade-down">
              <span className="inline-flex items-center gap-1 py-1 px-3 text-xs font-medium tracking-wider rounded-full bg-white/10 text-white/80 mb-6 neo-blur">
                <Sparkles className="h-3.5 w-3.5 text-blue-300" />
                AI-POWERED TEST AUTOMATION PLATFORM
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight max-w-4xl animate-fade-up">
              <span className="text-white">Write Tests in Plain English.</span>
              <span className="block text-white">Test Real Business Workflows.</span>
            </h1>

            <p className="text-xl text-gray-300 max-w-3xl mb-10 animate-fade-up">
              The only test platform where you can coordinate multiple browsers and roles in a single test. Describe what to test in plain English, SkyTest does the rest.
            </p>

            <a
              href="#waitlist"
              className="bg-blue-500 hover:bg-blue-600 py-4 px-8 rounded-full text-white font-medium text-lg transition-all duration-300 focus-ring animate-fade-up"
            >
              <span>Get Early Access</span>
            </a>

            <Link
              to="/features"
              className="mt-4 text-gray-400 hover:text-white transition-colors duration-300 animate-fade-up flex items-center gap-1"
            >
              See How It Works <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* Smooth gradient transition between hero and video section */}
        <div className="h-24 bg-gradient-to-b from-[#0a0a14] to-[#080812] -mt-2"></div>

        {/* Video Section */}
        <section className="py-0 relative bg-[#080812]">
          <div className="container mx-auto">
            <div className="mt-0 w-full max-w-4xl mx-auto relative animate-fade-up">
              <div className="w-full rounded-lg overflow-hidden shadow-lg">
                <AspectRatio ratio={16/9} className="w-full bg-[#080812]">
                  {videoReady && (
                    <video
                      ref={videoRef}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                      style={{ background: '#080812' }}
                    >
                      <source src="/skytestai-demo.webm" type="video/webm" />
                      <source src="/skytestai-demo.mp4" type="video/mp4" />
                    </video>
                  )}
                </AspectRatio>
              </div>
            </div>
          </div>
        </section>

        {/* Apply gradient background to next section for smooth transition */}
        <div className="h-24 bg-gradient-to-b from-[#080812] to-dark-bg"></div>

        {/* Problem Section */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -bottom-20 -right-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center animate-fade-up">
                <span className="text-gradient">Traditional Test Automation Is Broken</span>
              </h2>

              <div className="glass-morphism p-8 md:p-10 rounded-3xl animate-fade-up">
                <h3 className="text-2xl font-bold mb-6 text-white">
                  Why Your Tests Keep Breaking
                </h3>
                <p className="text-lg text-gray-300 leading-relaxed">
                  CSS selectors break on every UI change. Tests only cover single-user paths, but real business workflows involve multiple users interacting simultaneously. Maintaining test suites becomes a full-time job.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Features */}
        <section className="section-padding relative" id="features">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center animate-fade-up">
                <span className="text-gradient">What Makes SkyTest Different</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="glass-morphism p-8 rounded-2xl animate-fade-up">
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <MessageSquare className="text-white h-5 w-5" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-white">Plain English Test Creation</h4>
                  <p className="text-gray-300">Write tests like you'd describe them to a colleague. No code, no selectors, no brittle scripts.</p>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.1s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Users className="text-white h-5 w-5" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-white">Multi-Browser, Multi-Role Testing</h4>
                  <p className="text-gray-300">Coordinate multiple users across different browsers in one test. Test what actually happens when a customer buys and an admin verifies, simultaneously.</p>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.2s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Eye className="text-white h-5 w-5" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-white">AI-Powered Visual Navigation</h4>
                  <p className="text-gray-300">No CSS selectors or XPaths. AI sees and interacts with your app like a human would. It clicks buttons, fills forms, and reads text.</p>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.3s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Camera className="text-white h-5 w-5" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-white">Screenshot Evidence for Every Step</h4>
                  <p className="text-gray-300">Visual proof of every action for debugging and team reviews. See exactly what happened at each step.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center animate-fade-up">
                <span className="text-gradient">How It Works</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="glass-morphism p-6 md:p-8 rounded-2xl animate-fade-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <span className="text-3xl font-bold text-blue-400/70 mb-4 block">{step.number}</span>
                    <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                    <p className="text-gray-300">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Use Case Highlight */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -bottom-20 -right-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto relative z-10">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center animate-fade-up">
                <span className="text-gradient">Test What Actually Happens in Your Business</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Browser A - Customer */}
                <div className="glass-morphism p-8 rounded-2xl animate-fade-up border border-blue-500/20">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-3 w-3 rounded-full bg-blue-400"></div>
                    <h3 className="text-lg font-bold text-blue-300">Browser A: Customer</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <span className="text-blue-400/60 text-sm font-mono">1.</span>
                      <span className="text-gray-300">Sign in</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-blue-400/60 text-sm font-mono">2.</span>
                      <span className="text-gray-300">Add item to cart</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-blue-400/60 text-sm font-mono">3.</span>
                      <span className="text-gray-300">Complete checkout</span>
                    </div>
                  </div>
                </div>

                {/* Browser B - Admin */}
                <div className="glass-morphism p-8 rounded-2xl animate-fade-up border border-green-500/20" style={{ animationDelay: '0.1s' }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-3 w-3 rounded-full bg-green-400"></div>
                    <h3 className="text-lg font-bold text-green-300">Browser B: Admin</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <span className="text-green-400/60 text-sm font-mono">1.</span>
                      <span className="text-gray-300">Open admin portal</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-green-400/60 text-sm font-mono">2.</span>
                      <span className="text-gray-300">Verify new order appears</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-green-400/60 text-sm font-mono">3.</span>
                      <span className="text-gray-300">Update order status</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Coordination indicator */}
              <div className="flex justify-center mb-8 animate-fade-up">
                <div className="glass-morphism px-6 py-3 rounded-full inline-flex items-center gap-2 text-sm text-gray-400">
                  <div className="h-2 w-2 rounded-full bg-blue-400"></div>
                  <span>Coordinated in a single test</span>
                  <div className="h-2 w-2 rounded-full bg-green-400"></div>
                </div>
              </div>

              <div className="text-center animate-fade-up">
                <Link
                  to="/use-cases"
                  className="text-gray-400 hover:text-white transition-colors duration-300 inline-flex items-center gap-1"
                >
                  See More Use Cases <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="section-padding relative" id="how-it-works">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -bottom-20 -right-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center animate-fade-up">
                <span className="text-gradient">Key Benefits</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="relative glass-morphism rounded-2xl p-6 animate-fade-up overflow-hidden group hover:shadow-brand-sm transition-all duration-300"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Background glow effect */}
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-brand/5 rounded-full blur-[50px] group-hover:bg-brand/10 transition-all duration-500"></div>

                    <div className="relative z-10">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand/20 to-brand/40 flex items-center justify-center mb-4">
                        <benefit.icon className="h-6 w-6 text-white" />
                      </div>

                      <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                      <p className="text-gray-300">{benefit.description}</p>

                      {/* Decorative line */}
                      <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-brand/40 to-transparent w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <BlogSection posts={blogPosts} />

        {/* Final CTA */}
        <FinalCTA />
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Index;
